import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import SpinnerOverlay from '../../components/ui/SpinnerOverlay';


const LeaseRequestScreen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [refNumber, setRefNumber] = useState('');
  const [errors, setErrors] = useState({});

  const [company, setCompany] = useState({
    name: '',
    contactPerson: '',
    email: '',
    contactPhone: '',
    address: '',
  });

  const [requirements, setRequirements] = useState({
    vehicleTypes: [],
    quantity: 1,
    durationMonths: 12,
    estimatedMileage: 3000,
    chauffeur: false,
    insurance: false,
    maintenance: false,
    startDate: '',
    budgetRange: '',
    notes: '',
  });

  const vehicleTypeOptions = ['Sedan', 'SUV', 'Pickup', 'Van', 'Luxury', 'Minibus'];

  const toggleVehicleType = (type) => {
    setRequirements(r => ({
      ...r,
      vehicleTypes: r?.vehicleTypes?.includes(type)
        ? r?.vehicleTypes?.filter(t => t !== type)
        : [...r?.vehicleTypes, type],
    }));
  };

  const validate = () => {
    const errs = {};
    if (!company?.name?.trim()) errs.companyName = 'Company name required';
    if (!company?.email?.trim()) errs.email = 'Contact email required';
    if (!company?.contactPerson?.trim()) errs.contactPerson = 'Contact person required';
    if (requirements?.vehicleTypes?.length === 0) errs.vehicleTypes = 'Select at least one vehicle type';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Build requested_options array from boolean flags
      const requestedOptions = [];
      if (requirements?.chauffeur) requestedOptions?.push('Chauffeur Service');
      if (requirements?.insurance) requestedOptions?.push('Insurance Package');
      if (requirements?.maintenance) requestedOptions?.push('Maintenance Bundle');

      // Insert directly into corporate_lease_requests — NOT corporate_accounts
      const { error: reqError } = await supabase
        ?.from('corporate_lease_requests')
        ?.insert({
          user_id: user?.id || null,
          company_name: company?.name?.trim(),
          contact_name: company?.contactPerson?.trim(),
          email: company?.email?.trim(),
          phone: company?.contactPhone?.trim() || null,
          fleet_size: requirements?.quantity || 1,
          lease_term: requirements?.durationMonths || 12,
          notes: requirements?.notes?.trim() || null,
          requested_options: requestedOptions,
          intake_status: 'pending',
          // Store full requirements in the existing requirements JSONB field
          requirements: {
            vehicleTypes: requirements?.vehicleTypes,
            quantity: requirements?.quantity,
            durationMonths: requirements?.durationMonths,
            estimatedMileage: requirements?.estimatedMileage,
            startDate: requirements?.startDate,
            budgetRange: requirements?.budgetRange,
            address: company?.address,
          },
          req_status: 'submitted',
        });

      if (reqError) throw new Error(reqError?.message || 'Failed to submit lease request');

      const ref = 'LR-' + Date.now()?.toString()?.slice(-8);
      setRefNumber(ref);
      setShowSuccess(true);
      setTimeout(() => navigate('/corporate-leasing'), 4000);
    } catch (e) {
      setErrors({ submit: e?.message || 'Submission failed. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (err) =>
    `w-full px-4 py-3 rounded-xl text-white placeholder-white/30 text-sm outline-none focus:ring-2 focus:ring-[#ffd700]/40 ${err ? 'ring-1 ring-red-400/50' : ''}`;
  const inputStyle = { background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' };
  const labelClass = 'text-white/60 text-xs font-semibold uppercase tracking-wider mb-2 block';

  if (showSuccess) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0a1628' }}>
      <div className="text-center max-w-md">
        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(52,211,153,0.15)' }}>
          <Icon name="CheckCircle" size={40} className="text-[#34d399]" />
        </div>
        <h2 className="text-white font-black text-2xl mb-3">Request Submitted!</h2>
        <p className="text-white/50 text-base mb-4">
          Your lease request has been received. Our team will review it and contact you within 24 hours.
        </p>
        <div className="rounded-xl p-4 mb-6" style={{ background: 'rgba(255,215,0,0.08)', border: '1px solid rgba(255,215,0,0.2)' }}>
          <p className="text-white/40 text-xs mb-1">Reference Number</p>
          <p className="text-[#ffd700] font-black text-xl">{refNumber}</p>
        </div>
        <button onClick={() => navigate('/corporate-leasing')} className="btn-primary">
          Back to Corporate Leasing
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#0a1628', fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>
      {submitting && <SpinnerOverlay message="Submitting your request..." />}
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-white/10" style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/corporate-leasing')} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
            <Icon name="ArrowLeft" size={20} className="text-white" />
          </button>
          <div>
            <h1 className="text-white font-bold text-lg">Corporate Lease Request</h1>
            <p className="text-white/40 text-xs">Get a custom quote for your fleet</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Company Details */}
        <div className="rounded-2xl p-6 space-y-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <h2 className="text-white font-bold text-lg flex items-center gap-2">
            <Icon name="Building" size={20} className="text-[#ffd700]" /> Company Details
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelClass}>Company Legal Name *</label>
              <input
                className={inputClass(errors?.companyName)}
                style={inputStyle}
                placeholder="Acme Corporation Ltd"
                value={company?.name}
                onChange={e => setCompany(c => ({ ...c, name: e?.target?.value }))}
              />
              {errors?.companyName && <p className="text-red-400 text-xs mt-1">{errors?.companyName}</p>}
            </div>
            <div>
              <label className={labelClass}>Contact Email *</label>
              <input
                type="email"
                className={inputClass(errors?.email)}
                style={inputStyle}
                placeholder="finance@company.com"
                value={company?.email}
                onChange={e => setCompany(c => ({ ...c, email: e?.target?.value }))}
              />
              {errors?.email && <p className="text-red-400 text-xs mt-1">{errors?.email}</p>}
            </div>
            <div>
              <label className={labelClass}>Contact Person *</label>
              <input
                className={inputClass(errors?.contactPerson)}
                style={inputStyle}
                placeholder="John Kamau"
                value={company?.contactPerson}
                onChange={e => setCompany(c => ({ ...c, contactPerson: e?.target?.value }))}
              />
              {errors?.contactPerson && <p className="text-red-400 text-xs mt-1">{errors?.contactPerson}</p>}
            </div>
            <div>
              <label className={labelClass}>Contact Phone</label>
              <input
                className={inputClass()}
                style={inputStyle}
                placeholder="+254 7XX XXX XXX"
                value={company?.contactPhone}
                onChange={e => setCompany(c => ({ ...c, contactPhone: e?.target?.value }))}
              />
            </div>
            <div>
              <label className={labelClass}>Company Address</label>
              <input
                className={inputClass()}
                style={inputStyle}
                placeholder="Upper Hill, Nairobi"
                value={company?.address}
                onChange={e => setCompany(c => ({ ...c, address: e?.target?.value }))}
              />
            </div>
          </div>
        </div>

        {/* Fleet Requirements */}
        <div className="rounded-2xl p-6 space-y-5" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <h2 className="text-white font-bold text-lg flex items-center gap-2">
            <Icon name="Car" size={20} className="text-[#ffd700]" /> Fleet Requirements
          </h2>

          <div>
            <label className={labelClass}>Vehicle Types Needed *</label>
            <div className="flex flex-wrap gap-2">
              {vehicleTypeOptions?.map(type => (
                <button
                  key={type}
                  onClick={() => toggleVehicleType(type)}
                  className="px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                  style={{
                    background: requirements?.vehicleTypes?.includes(type) ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.06)',
                    color: requirements?.vehicleTypes?.includes(type) ? '#ffd700' : 'rgba(255,255,255,0.5)',
                    border: `1px solid ${requirements?.vehicleTypes?.includes(type) ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.1)'}`,
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
            {errors?.vehicleTypes && <p className="text-red-400 text-xs mt-1">{errors?.vehicleTypes}</p>}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Number of Vehicles</label>
              <input
                type="number"
                min={1}
                className={inputClass()}
                style={inputStyle}
                value={requirements?.quantity}
                onChange={e => setRequirements(r => ({ ...r, quantity: parseInt(e?.target?.value) || 1 }))}
              />
            </div>
            <div>
              <label className={labelClass}>Duration (months)</label>
              <input
                type="number"
                min={1}
                className={inputClass()}
                style={inputStyle}
                value={requirements?.durationMonths}
                onChange={e => setRequirements(r => ({ ...r, durationMonths: parseInt(e?.target?.value) || 12 }))}
              />
            </div>
            <div>
              <label className={labelClass}>Est. Monthly KM</label>
              <input
                type="number"
                className={inputClass()}
                style={inputStyle}
                value={requirements?.estimatedMileage}
                onChange={e => setRequirements(r => ({ ...r, estimatedMileage: parseInt(e?.target?.value) || 3000 }))}
              />
            </div>
            <div>
              <label className={labelClass}>Preferred Start Date</label>
              <input
                type="date"
                className={inputClass()}
                style={{ ...inputStyle, colorScheme: 'dark' }}
                value={requirements?.startDate}
                onChange={e => setRequirements(r => ({ ...r, startDate: e?.target?.value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Monthly Budget Range (KES)</label>
              <input
                className={inputClass()}
                style={inputStyle}
                placeholder="e.g. 200,000 - 500,000"
                value={requirements?.budgetRange}
                onChange={e => setRequirements(r => ({ ...r, budgetRange: e?.target?.value }))}
              />
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <label className={labelClass}>Additional Options</label>
            {[
              { key: 'chauffeur', label: 'Chauffeur Service', desc: 'Professional drivers for executive travel' },
              { key: 'insurance', label: 'Insurance Package', desc: 'Comprehensive fleet insurance included' },
              { key: 'maintenance', label: 'Maintenance Bundle', desc: 'Full service and maintenance included' },
            ]?.map(opt => (
              <label
                key={opt?.key}
                className="flex items-center justify-between p-4 rounded-xl cursor-pointer hover:bg-white/5 transition-all"
                style={{
                  border: `1px solid ${requirements?.[opt?.key] ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.08)'}`,
                  background: requirements?.[opt?.key] ? 'rgba(255,215,0,0.06)' : 'transparent',
                }}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={requirements?.[opt?.key]}
                    onChange={e => setRequirements(r => ({ ...r, [opt?.key]: e?.target?.checked }))}
                    className="w-4 h-4 accent-[#ffd700]"
                  />
                  <div>
                    <div className="text-white font-semibold text-sm">{opt?.label}</div>
                    <div className="text-white/40 text-xs">{opt?.desc}</div>
                  </div>
                </div>
              </label>
            ))}
          </div>

          {/* Notes */}
          <div>
            <label className={labelClass}>Additional Notes</label>
            <textarea
              className={inputClass()}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }}
              placeholder="Any specific requirements, preferred vehicle models, or questions..."
              value={requirements?.notes}
              onChange={e => setRequirements(r => ({ ...r, notes: e?.target?.value }))}
            />
          </div>
        </div>

        {errors?.submit && (
          <div className="p-4 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
            <p className="text-red-400 text-sm">{errors?.submit}</p>
          </div>
        )}

        <button onClick={handleSubmit} disabled={submitting} className="btn-primary-full text-base py-4">
          {submitting ? 'Submitting...' : 'Submit Lease Request'}
        </button>
        <p className="text-white/30 text-xs text-center pb-8">
          Our team will review your request and contact you within 24 hours with a custom quote.
        </p>
      </div>
    </div>
  );
};

export default LeaseRequestScreen;
